import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Layout,
  Space,
  Typography,
  Button,
  Avatar,
  Tooltip,
  Dropdown,
  Drawer,
  type MenuProps,
} from "antd";
import { UpgradeModal } from "./upgrade";
import { UserOutlined, DownOutlined, MenuOutlined, CloseOutlined } from "@ant-design/icons";
import Container from "./Container";
import { useAppSelector } from "../store/hooks";
import { useDisplayName } from "../hooks/useDisplayName";
import { useIsPremium } from "../hooks/useIsPremium";
import { isMembershipCurrent } from "../utils/billing";
import { authLogin, authLogout } from "../auth/authClient";
import { APP_ROUTES } from "../utils/appRoutes";
import { cn } from "../utils/cn";

const { Header } = Layout;

const NAV_LINKS = [
  { label: "Dashboard", path: APP_ROUTES.dashboard },
  { label: "Planner", path: APP_ROUTES.plan },
  { label: "My Dreams", path: APP_ROUTES.myDreams },
];

const Navbar = () => {
  const { isAuthenticated, isLoading, user } = useAppSelector((state) => state.auth);
  const isPremium = useIsPremium();
  const membershipReady = useAppSelector((state) => isMembershipCurrent(state.billing.membership));
  const displayName = useDisplayName();
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const isActive = (routePath: string) => {
    if (routePath === APP_ROUTES.dashboard) {
      return pathname === APP_ROUTES.home || pathname.startsWith(APP_ROUTES.dashboard);
    }
    return pathname.startsWith(routePath);
  };

  const navBtnClass = (routePath: string) =>
    cn(
      isActive(routePath)
        ? "rounded-lg bg-brand-overlay font-semibold text-brand-primary"
        : "text-text-primary",
    );

  const items: MenuProps["items"] = [
    { key: "profile", label: <Link to={APP_ROUTES.profile}>Profile</Link> },
    { key: "settings", label: <Link to={APP_ROUTES.settings}>Settings</Link> },
    { type: "divider" },
    { key: "logout", label: "Logout" },
  ];

  const handleMenuClick: MenuProps["onClick"] = ({ key }) => {
    if (key === "logout") {
      authLogout();
    }
  };

  const handleOpenUpgrade = (event: React.MouseEvent) => {
    event.stopPropagation();
    setUpgradeOpen(true);
  };

  const handleCloseUpgrade = () => {
    setUpgradeOpen(false);
  };

  const handleLogin = () => {
    authLogin();
  };

  const planBadge = (
    <>
      {isPremium === true ? (
        <Tooltip title="Premium member" placement="bottom">
          <span className="inline-flex items-center leading-none" aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="8" fill="#0088FF" />
              <polyline points="4.5,8.2 6.8,10.5 11.5,5.5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        </Tooltip>
      ) : isPremium === false && membershipReady ? (
        <Tooltip title="You are on the free plan. Click to upgrade to Premium." placement="bottom">
          <button
            type="button"
            className="inline-flex cursor-pointer border-0 bg-transparent p-0 leading-none"
            onClick={handleOpenUpgrade}
            aria-label="Open upgrade options"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="7" stroke="#9CA3AF" strokeWidth="1.5" />
              <polyline points="4.5,8.2 6.8,10.5 11.5,5.5" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </Tooltip>
      ) : null}
    </>
  );

  return (
    <>
      <Header
        className="border-b border-black/10 bg-surface-page px-6 pt-[14px] md:px-4 md:pt-0"
        data-testid="app-navbar"
      >
        <Container className="px-0">
          <div className="flex w-full items-center justify-between">
            {/* Logo */}
            <Link to={APP_ROUTES.home} className="inline-flex items-center">
              <img src="/dreamsup_logo.svg" alt="Dreams & Goals" className="block h-7 w-auto" />
            </Link>

            {/* Desktop nav */}
            <Space size="middle" align="center" wrap={false} className="hidden md:flex">

              {NAV_LINKS.map((link) => (
                <Link key={link.path} to={link.path}>
                  <Button type="text" className={navBtnClass(link.path)}>
                    {link.label}
                  </Button>
                </Link>
              ))}

              {isLoading ? (
                <div className="h-8 w-24 animate-pulse rounded-xl bg-slate-100" />
              ) : isAuthenticated ? (
                <Space align="center" className="ml-2">
                  <Avatar src={user?.picture} icon={!user?.picture && <UserOutlined />} />
                  <Dropdown menu={{ items, onClick: handleMenuClick }} trigger={["click"]} placement="bottomRight">
                    <div
                      className="flex cursor-pointer select-none items-center gap-2"
                      role="button"
                      tabIndex={0}
                      aria-label="User menu"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); (e.currentTarget as HTMLElement).click(); }
                      }}
                    >
                      <div className="flex flex-col items-start leading-none">
                        <Typography.Text className="text-sm font-semibold text-text-primary">{displayName}</Typography.Text>
                        {user?.email && <span className="mt-0.5 text-[11px] text-slate-400">{user.email}</span>}
                      </div>
                      {planBadge}
                      <DownOutlined className="text-[10px] text-text-primary" />
                    </div>
                  </Dropdown>
                </Space>
              ) : (
                <Button type="primary" ghost onClick={handleLogin}>Login</Button>
              )}
            </Space>

            {/* Mobile right side */}
            <div className="flex items-center gap-2 md:hidden">
              {isAuthenticated && (
                <>
                  <Avatar size="small" src={user?.picture} icon={!user?.picture && <UserOutlined />} />
                  {planBadge}
                </>
              )}
              <button
                type="button"
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border-0 bg-transparent text-text-primary"
                onClick={() => setMobileOpen(true)}
                aria-label="Open navigation"
              >
                <MenuOutlined style={{ fontSize: 18 }} />
              </button>
            </div>
          </div>
        </Container>
      </Header>

      {/* Mobile drawer */}
      <Drawer
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        placement="right"
        width={280}
        closeIcon={<CloseOutlined />}
        title={
          isAuthenticated ? (
            <div className="flex items-center gap-3">
              <Avatar src={user?.picture} icon={!user?.picture && <UserOutlined />} />
              <div className="flex flex-col leading-snug">
                <span className="text-[14px] font-semibold text-text-primary">{displayName}</span>
                {user?.email && <span className="text-[11px] text-slate-400">{user.email}</span>}
              </div>
            </div>
          ) : "Menu"
        }
        styles={{ body: { padding: 0 } }}
      >
        <nav className="flex flex-col">
          {NAV_LINKS.map((link) => (
            <button
              key={link.path}
              type="button"
              onClick={() => { navigate(link.path); setMobileOpen(false); }}
              className={cn(
                "flex cursor-pointer items-center border-0 bg-transparent px-6 py-4 text-left text-[15px] font-medium transition-colors",
                isActive(link.path)
                  ? "bg-brand-overlay font-semibold text-brand-primary"
                  : "text-text-primary hover:bg-slate-50"
              )}
            >
              {link.label}
            </button>
          ))}

          <div className="mx-6 my-2 border-t border-slate-100" />

          {isAuthenticated ? (
            <>
              <button
                type="button"
                onClick={() => { navigate(APP_ROUTES.profile); setMobileOpen(false); }}
                className="flex cursor-pointer items-center border-0 bg-transparent px-6 py-4 text-left text-[15px] font-medium text-text-primary transition-colors hover:bg-slate-50"
              >
                Profile
              </button>
              <button
                type="button"
                onClick={() => { navigate(APP_ROUTES.settings); setMobileOpen(false); }}
                className="flex cursor-pointer items-center border-0 bg-transparent px-6 py-4 text-left text-[15px] font-medium text-text-primary transition-colors hover:bg-slate-50"
              >
                Settings
              </button>
              {isPremium === false && membershipReady && (
                <div className="px-6 py-3">
                  <button
                    type="button"
                    onClick={() => { setMobileOpen(false); setUpgradeOpen(true); }}
                    className="w-full cursor-pointer rounded-xl border-0 px-4 py-2.5 text-[14px] font-semibold text-white transition-colors"
                    style={{ background: "#0088FF" }}
                  >
                    Upgrade to Premium
                  </button>
                </div>
              )}
              <div className="mx-6 my-2 border-t border-slate-100" />
              <button
                type="button"
                onClick={() => { authLogout(); setMobileOpen(false); }}
                className="flex cursor-pointer items-center border-0 bg-transparent px-6 py-4 text-left text-[15px] font-medium text-red-500 transition-colors hover:bg-red-50"
              >
                Logout
              </button>
            </>
          ) : (
            <div className="px-6 py-4">
              <button
                type="button"
                onClick={() => { authLogin(); setMobileOpen(false); }}
                className="w-full cursor-pointer rounded-xl border border-brand-primary bg-transparent px-4 py-2.5 text-[14px] font-semibold text-brand-primary"
              >
                Login
              </button>
            </div>
          )}
        </nav>
      </Drawer>

      <UpgradeModal open={upgradeOpen} trigger="banner" onClose={handleCloseUpgrade} />
    </>
  );
};

export default Navbar;
